import { Icon } from "@iconify/react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode, useEffect, useId, useState } from "react";
import { useTranslations } from "use-intl";
import { getCurrentUser } from "$/modules/appwrite/lib/appwrite";
import { AssetPosition } from "$/modules/appwrite/types/collections";
import type { ExamResult, ExamStart } from "$/modules/exam/lib/exam";
import { startExam, submitExam } from "$/modules/exam/lib/exam";
import { QuestionMedia } from "$/modules/question/ui/question-media";
import { Button } from "$/modules/shared/ui/button";
import { Card, CardContent } from "$/modules/shared/ui/card";
import { Field, FieldLabel } from "$/modules/shared/ui/field";
import { Input } from "$/modules/shared/ui/input";
import { Progress } from "$/modules/shared/ui/progress";
import { Spinner } from "$/modules/shared/ui/spinner";

export const Route = createFileRoute("/$locale/exam/$token/")({
	component: ExamPage,
});

function ExamPage() {
	const fieldId = useId();

	const t = useTranslations("exam");
	const { token, locale } = Route.useParams();
	const [exam, setExam] = useState<ExamStart>();
	const [questionIndex, setQuestionIndex] = useState(0);
	const [answers, setAnswers] = useState<Record<string, string[]>>({});
	const [result, setResult] = useState<ExamResult>();
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [requiresAuthentication, setRequiresAuthentication] = useState(false);
	const [participantUserId, setParticipantUserId] = useState<string>();

	useEffect(() => {
		void getCurrentUser().then((user) => setParticipantUserId(user?.$id));
	}, []);

	const begin = async () => {
		setIsLoading(true);
		setError("");
		setRequiresAuthentication(false);
		try {
			setExam(await startExam(token));
		} catch (cause) {
			const message = cause instanceof Error ? cause.message : t("startError");
			setError(message);
			setRequiresAuthentication(
				message === "Für diese Prüfung ist eine Anmeldung erforderlich.",
			);
		} finally {
			setIsLoading(false);
		}
	};
	const current = exam?.questions[questionIndex];
	const picked = current ? (answers[current.id] ?? []) : [];
	const setPicked = (values: string[]) =>
		current &&
		setAnswers((previous) => ({ ...previous, [current.id]: values }));
	const next = async () => {
		if (!exam || !current) return;
		if (questionIndex < exam.questions.length - 1) {
			setQuestionIndex((index) => index + 1);
			return;
		}
		setIsLoading(true);
		setError("");
		try {
			setResult(await submitExam(token, exam.attemptId, answers));
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : t("submitError"));
		} finally {
			setIsLoading(false);
		}
	};

	if (result)
		return (
			<ExamShell>
				<Card className="w-full max-w-xl border border-default-200">
					<CardContent className="gap-4 p-8 text-center">
						<Icon
							icon={result.passed ? "lucide:badge-check" : "lucide:circle-x"}
							className={`mx-auto size-12 ${result.passed ? "text-success" : "text-danger"}`}
						/>
						<p className="text-xs font-extrabold uppercase tracking-[0.16em] text-primary">
							{t("result")}
						</p>
						<h1 className="font-display text-4xl font-bold">
							{result.passed ? t("passed") : t("failed")}
						</h1>
						<p className="text-lg text-foreground-600">
							{result.score} / {result.maxScore} · {result.percentage}%
						</p>
						{participantUserId && (
							<Button asChild variant="default">
								<Link
									to="/$locale/$userId/profile/certificates"
									params={{ locale, userId: participantUserId }}
								>
									{<Icon icon="lucide:history" />}
									{t("viewHistory")}
								</Link>
							</Button>
						)}
					</CardContent>
				</Card>
			</ExamShell>
		);
	if (!exam)
		return (
			<ExamShell>
				<Card className="w-full max-w-md border border-default-200">
					<CardContent className="gap-4 p-8">
						<p className="text-xs font-extrabold uppercase tracking-[0.16em] text-primary">
							{t("label")}
						</p>
						<h1 className="font-display text-3xl font-bold">{t("start")}</h1>
						<p className="text-foreground-500">{t("startDescription")}</p>
						<Button
							onClick={begin}
							disabled={isLoading}
							variant="default"
							type="button"
						>
							{isLoading && <Spinner data-icon="inline-start" />}
							{t("begin")}
						</Button>
						{error && <p className="text-sm text-danger">{error}</p>}
						{requiresAuthentication && (
							<div className="flex flex-wrap gap-2">
								<Button variant="default" asChild>
									<a
										href={`/${locale}/signin?redirect=${encodeURIComponent(`/${locale}/exam/${token}`)}`}
									>
										{t("signIn")}
									</a>
								</Button>
								<Button variant="muted" asChild>
									<a
										href={`/${locale}/signup?redirect=${encodeURIComponent(`/${locale}/exam/${token}`)}`}
									>
										{t("createAccount")}
									</a>
								</Button>
							</div>
						)}
					</CardContent>
				</Card>
			</ExamShell>
		);
	if (!current) return null;
	const canContinue = current.questionType === "Info" || picked.length > 0;
	return (
		<ExamShell>
			<main className="w-full max-w-4xl">
				<header className="mb-5">
					<p className="text-xs font-extrabold uppercase tracking-[0.16em] text-primary">
						{exam.project.name}
					</p>
					<div className="mt-2 flex justify-between text-sm text-foreground-500">
						<span>
							{t("questionProgress", {
								current: questionIndex + 1,
								total: exam.questions.length,
							})}
						</span>
						<span>{exam.participantName}</span>
					</div>
					<Progress
						className="mt-2"
						value={((questionIndex + 1) / exam.questions.length) * 100}
					/>
				</header>
				<Card className="border border-default-200">
					<CardContent className="gap-6 p-6 sm:p-10">
						<QuestionMedia
							question={current}
							position={AssetPosition.BeforeQuestion}
						/>
						<h1 className="font-display text-3xl font-bold">
							{current.question}
						</h1>
						<QuestionMedia
							question={current}
							position={AssetPosition.AfterQuestion}
						/>
						{current.questionType === "Text" ? (
							<Field>
								<FieldLabel htmlFor={`${fieldId}-100`}>
									{t("yourAnswer")}
								</FieldLabel>
								<Input
									id={`${fieldId}-100`}
									value={picked[0] ?? ""}
									onChange={(event) =>
										((value) => setPicked(value.trim() ? [value] : []))(
											event.currentTarget.value,
										)
									}
								/>
							</Field>
						) : current.questionType === "Info" ? (
							<p className="text-foreground-500">{t("infoHint")}</p>
						) : (
							<div className="grid gap-3 sm:grid-cols-2">
								{current.options.map((option, index) => {
									const selected = picked.includes(option);
									return (
										<Button
											key={option}
											className="h-auto min-h-16 justify-start whitespace-normal p-4 text-left"
											onClick={() =>
												current.questionType === "Multiple"
													? setPicked(
															selected
																? picked.filter((item) => item !== option)
																: [...picked, option],
														)
													: setPicked([option])
											}
											variant={selected ? "default" : "muted"}
											type="button"
										>
											<span className="mr-3 font-bold text-foreground-400">
												{index + 1}
											</span>
											{option}
										</Button>
									);
								})}
							</div>
						)}
						<div className="flex justify-end">
							<Button
								onClick={next}
								disabled={!canContinue || isLoading}
								variant="default"
								type="button"
							>
								{isLoading && <Spinner data-icon="inline-start" />}
								{questionIndex === exam.questions.length - 1
									? t("submit")
									: t("nextQuestion")}
								{<Icon icon="lucide:arrow-right" />}
							</Button>
						</div>
						{error && <p className="text-sm text-danger">{error}</p>}
					</CardContent>
				</Card>
			</main>
		</ExamShell>
	);
}

function ExamShell({ children }: { children: ReactNode }) {
	return (
		<div className="grid min-h-screen place-items-center bg-content2 p-4 sm:p-8">
			{children}
		</div>
	);
}
